import { useMemo, useRef, useState } from "react";
import {
  Box,
  FormControl,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  OutlinedInput,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

const parseDate = (dateStr) => {
  if (!dateStr) return null;

  const parsed = new Date(dateStr);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const ddMmYyyyMatch = String(dateStr).match(/^(\\d{1,2})[-/](\\d{1,2})[-/](\\d{4})$/);
  if (ddMmYyyyMatch) {
    const [, day, month, year] = ddMmYyyyMatch;
    const fallback = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  return null;
};

const groupByMonth = (options = []) => {
  const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });
  const groupedMap = new Map();

  options.forEach((option) => {
    const parsedDate = parseDate(option?.value);
    const groupKey = parsedDate
      ? `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}`
      : "other_dates";
    const groupLabel = parsedDate ? monthFormatter.format(parsedDate) : "Other Dates";

    const group = groupedMap.get(groupKey) || { key: groupKey, label: groupLabel, options: [] };
    group.options.push(option);
    groupedMap.set(groupKey, group);
  });

  return Array.from(groupedMap.values());
};

const MonthDateDropdown = ({ label, value, options = [], handleChange, disabled, ...props }) => {
  const anchorRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedMonthKey, setExpandedMonthKey] = useState("");

  const groupedOptions = useMemo(() => groupByMonth(options), [options]);

  const selectedLabel = useMemo(() => {
    const selected = options.find((option) => option?.value === value);
    return selected?.label || "";
  }, [options, value]);

  const toggleMonth = (monthKey) => {
    setExpandedMonthKey((prev) => (prev === monthKey ? "" : monthKey));
  };

  const onDateSelect = (selectedValue) => {
    handleChange({ target: { value: selectedValue } });
    setMenuOpen(false);
  };

  return (
    <>
      <FormControl
        size="small"
        disabled={disabled}
        sx={{
          width: props?.width || "100%",
          maxWidth: props?.maxWidth || "150px",
          ...props.styles,
        }}
      >
        <InputLabel>{label}</InputLabel>
        <OutlinedInput
          label={label}
          value={selectedLabel}
          readOnly
          onClick={() => {
            if (!disabled) setMenuOpen(true);
          }}
          inputRef={anchorRef}
          sx={{
            height: props?.height || "40px",
            minHeight: props?.height || "40px",
            cursor: disabled ? "not-allowed" : "pointer",
            "& .MuiOutlinedInput-input": {
              cursor: disabled ? "not-allowed" : "pointer",
            },
          }}
          endAdornment={(
            <InputAdornment position="end">
              <KeyboardArrowDownIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.54)" }} />
            </InputAdornment>
          )}
        />
      </FormControl>

      <Menu
        open={menuOpen}
        anchorEl={anchorRef.current}
        onClose={() => setMenuOpen(false)}
        PaperProps={{ sx: { width: props?.width || "200px", maxHeight: 360 } }}
      >
        {groupedOptions.length === 0 && (
          <MenuItem disabled>
            <Typography fontSize={13}>No dates available</Typography>
          </MenuItem>
        )}

        {groupedOptions.map((group) => {
          const isExpanded = expandedMonthKey === group.key;
          return (
            <Box key={group.key}>
              <MenuItem onClick={() => toggleMonth(group.key)} sx={{ minHeight: 34, fontWeight: 600 }}>
                {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                <Typography fontSize={13} sx={{ ml: 0.5 }}>
                  {group.label}
                </Typography>
              </MenuItem>
              {isExpanded && group.options.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => onDateSelect(option.value)}
                  sx={{ pl: 4.5, minHeight: 32 }}
                >
                  <Typography fontSize={13}>{option.label}</Typography>
                </MenuItem>
              ))}
            </Box>
          );
        })}
      </Menu>
    </>
  );
};

export default MonthDateDropdown;
